import { ApiError, upload } from './api';
import type { Checkout, Page, PersonSummary, Ref, WorkOrderStatus } from './types';

export type CheckoutKind = 'STANDARD' | 'LOANER';
export type LoanerReason = 'REPAIR' | 'FORGOT_DEVICE' | 'OTHER';
export type AlertLevel = 'OFF' | 'WARN' | 'BLOCK';
export type PersonFieldType = 'CHECKBOX' | 'DATE' | 'TEXT' | 'SELECT';
export type FieldAlertRule = 'NONE' | 'CHECKED' | 'DATE_PASSED' | 'FILLED' | 'EQUALS';
export type IntakeQuestionType = 'CHECKBOX' | 'SELECT' | 'TEXT';
export type ReminderStage = 'PRE_DUE' | 'DUE' | 'OVERDUE' | 'ESCALATED' | 'MANUAL';
export type ReminderStatus = 'SENT' | 'SKIPPED' | 'FAILED';

/** The most condition photos one check-in keeps. */
export const MAX_CHECKIN_PHOTOS = 10;

export const LOANER_REASON_LABEL: Record<LoanerReason, string> = {
  REPAIR: 'Their device is being repaired',
  FORGOT_DEVICE: 'Forgot their device today',
  OTHER: 'Something else',
};

export const LOANER_REASON_SHORT: Record<LoanerReason, string> = { REPAIR: 'Repair', FORGOT_DEVICE: 'Forgot device', OTHER: 'Other' };

export const REMINDER_STAGE_LABEL: Record<ReminderStage, string> = {
  PRE_DUE: 'Due soon',
  DUE: 'Due today',
  OVERDUE: 'Overdue',
  ESCALATED: 'Last reminder',
  MANUAL: 'Sent by hand',
};

export const ALERT_LEVEL_LABEL: Record<AlertLevel, string> = { OFF: 'Off', WARN: 'Warn', BLOCK: 'Block the check-out' };

export const PERSON_FIELD_TYPE_LABEL: Record<PersonFieldType, string> = { CHECKBOX: 'Checkbox', DATE: 'Date', TEXT: 'Text', SELECT: 'Choice' };

export const ALERT_RULE_LABEL: Record<FieldAlertRule, string> = {
  NONE: 'No alert',
  CHECKED: 'When it’s checked',
  DATE_PASSED: 'Once the date has passed',
  FILLED: 'Whenever it has a value',
  EQUALS: 'When it’s a certain value',
};

/** The alerts that make sense for each kind of field. */
export const ALERT_RULES_FOR: Record<PersonFieldType, FieldAlertRule[]> = {
  CHECKBOX: ['NONE', 'CHECKED'],
  DATE: ['NONE', 'DATE_PASSED', 'FILLED'],
  TEXT: ['NONE', 'FILLED', 'EQUALS'],
  SELECT: ['NONE', 'FILLED', 'EQUALS'],
};

export const INTAKE_TYPE_LABEL: Record<IntakeQuestionType, string> = { CHECKBOX: 'Yes or no', SELECT: 'Choice', TEXT: 'Short answer' };

export interface BorrowerAlert {
  key: string;
  level: 'WARN' | 'BLOCK';
  title: string;
  detail: string | null;
}

/** The alerts on a borrower when something went out to them, and whether an administrator overrode one that blocks. */
export interface AlertsAtCheckout {
  alerts: BorrowerAlert[];
  overridden: boolean;
}

/** A check-in form question as it was asked, and its answer. */
export interface IntakeAnswer {
  questionId: string;
  label: string;
  type: IntakeQuestionType;
  answer: string;
}

export interface PersonField {
  id: string;
  name: string;
  description: string | null;
  type: PersonFieldType;
  options: string[];
  alertRule: FieldAlertRule;
  alertValue: string | null;
  alertLevel: AlertLevel;
  alertMessage: string | null;
  archived: boolean;
  sortOrder: number;
}

export interface PersonFieldListItem extends PersonField {
  personCount: number;
}

export interface PersonFields {
  fields: (PersonField & { value: string | null })[];
  alerts: BorrowerAlert[];
}

export interface CheckinQuestion {
  id: string;
  label: string;
  type: IntakeQuestionType;
  options: string[];
  required: boolean;
  archived: boolean;
  sortOrder: number;
}

export interface ReminderPolicy {
  enabled: boolean;
  preDueDays: number | null;
  dueDay: boolean;
  overdueDays: number | null;
  escalateDays: number | null;
  escalateEmails: string[];
  notifyStaff: boolean;
}

export interface CheckoutSettings {
  openBalanceAlert: AlertLevel;
  overdueDevicesAlert: AlertLevel;
  loanerDays: number;
  sameDayReturnTime: string;
  standardReminders: ReminderPolicy;
  loanerReminders: ReminderPolicy;
  mailbox: { ready: boolean; address: string | null };
}

export interface CheckoutReminder {
  id: string;
  stage: ReminderStage;
  status: ReminderStatus;
  toAddress: string | null;
  error: string | null;
  sentByName: string | null;
  createdAt: string;
}

export interface Loaner extends Checkout {
  reminders: CheckoutReminder[];
}

export interface LoanerPage extends Page<Loaner> {
  summary: { active: number; dueSoon: number; overdue: number; escalated: number; returnedThisWeek: number; available: number; pool: number };
}

export interface LoanerTicket {
  id: string;
  number: number;
  title: string;
  status: WorkOrderStatus;
  asset: { id: string; assetTag: string; name: string } | null;
}

export interface LoanerOptions {
  defaults: { sameDayDueAt: string; repairDueAt: string; loanerDays: number; sameDayReturnTime: string };
  devices: { id: string; assetTag: string; name: string; serialNumber: string | null; category: Ref | null }[];
  poolSize: number;
  tickets: LoanerTicket[];
  ticket: (LoanerTicket & { requesterPerson: PersonSummary | null }) | null;
}

/** A loaner as its ticket lists it. */
export interface TicketLoaner {
  id: string;
  loanerReason: LoanerReason | null;
  checkedOutAt: string;
  dueAt: string | null;
  checkedInAt: string | null;
  assigneeLabel: string;
  asset: { id: string; assetTag: string; name: string };
}

export interface GradeOptions {
  classYears: { gradYear: number; students: number }[];
  grades: { gradeLevel: string; students: number }[];
  categories: { id: string; name: string; available: number }[];
  models: { id: string; name: string; categoryId: string | null; available: number }[];
  available: number;
}

export type RolloutStatus = 'READY' | 'HAS_DEVICE' | 'BLOCKED' | 'NO_DEVICE';

export interface RolloutRow {
  person: PersonSummary;
  status: RolloutStatus;
  asset: { id: string; assetTag: string; name: string; serialNumber: string | null; category: Ref | null } | null;
  holding: string[];
  alerts: BorrowerAlert[];
}

export interface RolloutPreview {
  label: string;
  rows: RolloutRow[];
  truncated: boolean;
  summary: { students: number; ready: number; hasDevice: number; blocked: number; noDevice: number; warned: number; available: number };
}

export interface BulkCheckoutResult {
  checkedOut: { checkoutId: string; personId: string; assetId: string; assetTag: string; assigneeLabel: string }[];
  failed: { personId: string; assetId: string; error: string }[];
}

export interface ReturnsList {
  label: string;
  items: Checkout[];
  total: number;
  overdue: number;
  truncated: boolean;
}

export interface BulkCheckinResult {
  checkedIn: { checkoutId: string; assetTag: string; assigneeLabel: string; daysLate: number | null }[];
  failed: { checkoutId: string; error: string }[];
}

export function uploadCheckoutPhoto(photo: Blob, fileName: string) {
  return upload<{ id: string; fileName: string }>('/checkouts/photos', photo, { 'x-photo': encodeURIComponent(JSON.stringify({ fileName })) });
}

/** Whole days something came back after it was due (0 for less than a day), or null when it wasn't late. */
export function daysLate(checkout: { dueAt: string | null; checkedInAt: string | null }) {
  if (!checkout.dueAt || !checkout.checkedInAt) return null;
  const late = new Date(checkout.checkedInAt).getTime() - new Date(checkout.dueAt).getTime();
  return late > 0 ? Math.floor(late / 86_400_000) : null;
}

/** What checking out to a room did to the devices' home location. */
export interface HomeLocationResult {
  label: string;
  moved: number;
  /** Devices kept on a cart, which stay with it. */
  onCarts: string[];
}

/** The end of a check-out's message, about the home location. */
export function homeLocationNote(home: HomeLocationResult | null | undefined) {
  if (!home) return '';
  const parts = [home.moved > 0 && `home location set to ${home.label}`, home.onCarts.length > 0 && `${home.onCarts.join(', ')} ${home.onCarts.length === 1 ? 'stays' : 'stay'} with ${home.onCarts.length === 1 ? 'its' : 'their'} cart`];
  const text = parts.filter(Boolean).join('; ');
  return text ? ` - ${text}` : '';
}

export const lateLabel = (days: number) => (days < 1 ? 'Returned late' : `${days} day${days === 1 ? '' : 's'} late`);

/** The alerts that stopped a check-out, when that's why it was refused. */
export function alertsFromError(err: unknown) {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  return (err.details as { alerts?: BorrowerAlert[] } | undefined)?.alerts ?? null;
}

/** A check-in's answers in one line. */
export function intakeSummary(intake: IntakeAnswer[] | null | undefined) {
  return (intake ?? []).map((answer) => `${answer.label}${/[?:]$/.test(answer.label) ? '' : ':'} ${answer.answer}`).join(' · ');
}
